/**
 * Друкована форма «Звіт виробництва за зміну» (довільна форма, ч.669):
 * організація, № і дата, склад/підрозділ, секції Продукція (к-сть,
 * планова/фактична вартість) + Матеріали (к-сть, собівартість для
 * проведеного) + Поворотні відходи; підписи. Єдиний стиль друку.
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productionReports } from '@/api/productionReports';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { departmentsApi } from '@/api/hr';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmtQty = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 3 });
const fmtMoney = (n: number | string | null | undefined) =>
  Number(n ?? 0).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ProductionReportPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['production-report', id],
    queryFn: () => productionReports.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const whQ = useQuery({
    queryKey: ['warehouses', d?.organization_id],
    queryFn: () => refs.warehouses(d!.organization_id),
    enabled: !!d?.organization_id,
  });
  const deptQ = useQuery({
    queryKey: ['departments', d?.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: d!.organization_id, limit: 500 }),
    enabled: !!d?.organization_id,
  });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження звіту…</div>;
  }

  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  const wh = (whQ.data ?? []).find((w) => w.id === d.warehouse_id);
  const dept = deptQ.data?.items.find((x) => x.id === d.department_id);
  const uomOf = (it: { nomenclature_id: string; uom_id?: string | null }) => {
    if (it.uom_id) return uomById.get(it.uom_id) ?? 'шт';
    const n = nomById.get(it.nomenclature_id);
    return (n?.base_uom_id && uomById.get(n.base_uom_id)) || 'шт';
  };
  const nomName = (nid: string) => {
    const n = nomById.get(nid);
    return n ? n.name : '—';
  };
  const prodTotal = d.products.reduce((s, p) => s + Number(p.total_cost ?? p.planned_amount ?? 0), 0);
  const matTotal = d.materials.reduce((s, m) => s + Number(m.total_cost ?? 0), 0);
  const wasteTotal = (d.returnable_waste ?? []).reduce((s, w) => s + Number(w.amount ?? 0), 0);

  return (
    <div className="print-page" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .pr-table { width: 100%; border-collapse: collapse; margin: 0.35rem 0 0.75rem; font-size: 9pt; line-height: 1.15; }
        .pr-table th, .pr-table td { border: 1px solid #000; padding: 1px 4px; }
        .pr-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
        .pr-sec { font-weight: 700; margin: 0.6rem 0 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.production_reports',
        documentId: d.id,
        filename: `Звіт виробництва ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '10pt' }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0' }}>
        Звіт виробництва за зміну № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>

      <table style={{ width: '100%', fontSize: '10pt', marginBottom: '0.25rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%' }}>
              <span style={{ color: '#555' }}>Склад: </span>{wh?.name ?? '—'}
            </td>
            <td>
              <span style={{ color: '#555' }}>Підрозділ: </span>{dept?.name ?? '—'}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="pr-sec">Вироблена продукція</div>
      <table className="pr-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Продукція</th>
            <th style={{ width: 45 }}>Од.</th>
            <th style={{ width: 80 }}>К-сть</th>
            <th style={{ width: 90 }}>Планова сума</th>
            {d.posted && <th style={{ width: 95 }}>Собівартість</th>}
          </tr>
        </thead>
        <tbody>
          {d.products.map((p, i) => (
            <tr key={i}>
              <td style={{ textAlign: 'center' }}>{i + 1}</td>
              <td>{nomName(p.nomenclature_id)}</td>
              <td style={{ textAlign: 'center' }}>{uomOf(p)}</td>
              <td style={{ textAlign: 'right' }}>{fmtQty(p.quantity)}</td>
              <td style={{ textAlign: 'right' }}>
                {p.planned_amount != null ? fmtMoney(p.planned_amount)
                  : p.planned_cost != null ? fmtMoney(Number(p.planned_cost) * Number(p.quantity)) : '—'}
              </td>
              {d.posted && <td style={{ textAlign: 'right' }}>{fmtMoney(p.total_cost)}</td>}
            </tr>
          ))}
        </tbody>
        {d.posted && (
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={5} style={{ textAlign: 'right' }}>Разом випуск:</td>
              <td style={{ textAlign: 'right' }}>{fmtMoney(prodTotal)}</td>
            </tr>
          </tfoot>
        )}
      </table>

      {d.materials.length > 0 && (<>
        <div className="pr-sec">Списані матеріали</div>
        <table className="pr-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}>№</th>
              <th>Матеріал</th>
              <th style={{ width: 45 }}>Од.</th>
              <th style={{ width: 80 }}>К-сть</th>
              {d.posted && <th style={{ width: 95 }}>Собівартість</th>}
            </tr>
          </thead>
          <tbody>
            {d.materials.map((m, i) => (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nomName(m.nomenclature_id)}</td>
                <td style={{ textAlign: 'center' }}>{uomOf(m)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(m.quantity)}</td>
                {d.posted && <td style={{ textAlign: 'right' }}>{fmtMoney(m.total_cost)}</td>}
              </tr>
            ))}
          </tbody>
          {d.posted && (
            <tfoot>
              <tr style={{ fontWeight: 700 }}>
                <td colSpan={4} style={{ textAlign: 'right' }}>Разом матеріали:</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(matTotal)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </>)}

      {(d.returnable_waste ?? []).length > 0 && (<>
        <div className="pr-sec">Поворотні відходи</div>
        <table className="pr-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}>№</th>
              <th>Відходи</th>
              <th style={{ width: 45 }}>Од.</th>
              <th style={{ width: 80 }}>К-сть</th>
              <th style={{ width: 95 }}>Сума</th>
            </tr>
          </thead>
          <tbody>
            {(d.returnable_waste ?? []).map((w, i) => (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nomName(w.nomenclature_id)}</td>
                <td style={{ textAlign: 'center' }}>{uomOf(w)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(w.quantity)}</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(w.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={4} style={{ textAlign: 'right' }}>Разом відходи:</td>
              <td style={{ textAlign: 'right' }}>{fmtMoney(wasteTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </>)}

      {d.comment && <p style={{ margin: '0.5rem 0', color: '#333' }}>Коментар: {d.comment}</p>}

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Здав (майстер зміни)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Прийняв (МВО складу)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
